import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { get } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getPolicy } from "@store/Store/Data/getPolicy";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    canSeeAccountSummary: getPolicy(state, "Account.CanSeeAccountSummary"),
});

type Props = ReturnType<typeof mapStateToProps>;

export interface AccountSummaryStyles {
    summaryWrapper?: InjectableCss;
    summaryContainer?: GridContainerProps;
    summaryLeftColumn?: GridItemProps;
    summaryMiddleColumn?: GridItemProps;
    summaryRightColumn?: GridItemProps;
    summaryLineContainer?: GridContainerProps;
    summaryLineLabel?: GridItemProps;
    summaryLineValue?: GridItemProps;
    customerInfoContainer?: InjectableCss;
    customerInfoLine?: InjectableCss;
    customerAddress?: InjectableCss;
    addressLine?: TypographyProps;
    addressNameLine?: TypographyProps;
    summaryLineLabelText?: TypographyProps;
    summaryLineValueText?: TypographyProps;
    summaryLineLabelTotalText?: TypographyProps;
    summaryLineValueTotalText?: TypographyProps;
    addressInfoLabel?: TypographyProps;
    addressInfoValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
}

const summaryTableStyles = `
    flex-direction: column;
    border-left: 1px solid ${COLORS.border.base};
    padding-top: 0;
    padding-bottom: 0;

    > div:nth-child(odd) {
        background-color: ${COLORS.background.alt};
    }
`;

export const accountSummaryStyles: AccountSummaryStyles = {
    summaryWrapper: {
        css: css`
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["75px", null, null, "120px", null], properties: ["margin-bottom"] }],
                    mediaOptions.min,
                )};
        `,
    },
    summaryContainer: {
        gap: 60,
        css: css`
            padding-top: 30px;
        `,
    },
    summaryLeftColumn: {
        width: [12, 12, 12, 4, 4],
        css: css`
            padding-top: 0;
            padding-bottom: 0;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["column", null, "row", "column", null], properties: ["flex-direction"] }],
                    mediaOptions.min,
                )}
        `,
    },
    summaryMiddleColumn: {
        width: [12, 12, 6, 4, 4],
        css: css`
            ${summaryTableStyles}

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["0", null, null, "1px", null], properties: ["border-width"] },
                        {
                            values: ["20px !important", null, null, "0 !important", null],
                            properties: ["margin-bottom"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    summaryRightColumn: {
        width: [12, 12, 6, 4, 4],
        css: css`
            ${summaryTableStyles}

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["0", null, "1px", null, null], properties: ["border-width"] }],
                    mediaOptions.min,
                )}
        `,
    },
    summaryLineContainer: {
        gap: 0,
        css: css`
            flex-grow: inherit;
            border-right: 1px solid ${COLORS.border.base};
            border-left: 1px solid ${COLORS.border.base};

            &:first-child {
                border-top: 1px solid ${COLORS.border.base};
            }

            &:last-child {
                border-bottom: 1px solid ${COLORS.border.base};
            }

            ${GridOffset} {
                min-height: 36px;
            }
        `,
    },
    customerInfoContainer: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["30px", null, null, "0", null], properties: ["margin-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    customerAddress: {
        css: css`
            display: flex;
            flex-direction: column;
            min-width: 50%;
            margin-bottom: 30px;
        `,
    },
    customerInfoLine: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    addressLine: {
        size: TYPOGRAPHY.size.base,
    },
    addressNameLine: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.large,
        weight: TYPOGRAPHY.weight.medium,
    },
    addressInfoLabel: {
        color: COLORS.text.dark,
        weight: TYPOGRAPHY.weight.bold,
    },
    addressInfoValue: {},
    summaryLineLabel: {
        width: 6,
        align: "middle",
        css: css`
            padding: 5px 10px;
        `,
    },
    summaryLineValue: {
        width: 6,
        align: "middle",
        css: css`
            justify-content: flex-end;
            padding: 5px 10px;
        `,
    },
    summaryLineLabelText: {
        size: TYPOGRAPHY.size.base,
    },
    summaryLineValueText: {
        size: TYPOGRAPHY.size.base,
    },
    summaryLineLabelTotalText: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.base,
        weight: TYPOGRAPHY.weight.bold,
    },
    summaryLineValueTotalText: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.base,
        weight: TYPOGRAPHY.weight.bold,
    },
};

const styles = accountSummaryStyles;

const AccountSummary = ({ canSeeAccountSummary }: Props) => {
    if (!canSeeAccountSummary) {
        return null;
    }

    const [summary, setSummary] = useState<any>([]);

    useEffect(() => {
        const accountsUrl = "/api/v1/accounts/Current/Summary";
        const summary = async () => {
            const data = await get(accountsUrl);
            setSummary(data);
        };
        summary();
    }, []);

    if (summary.customer === null) {
        return null;
    }

    if (summary.length === 0) {
        return <LoadingSpinner {...styles.loadingSpinner} />;
    }

    return (
        <StyledWrapper {...styles.summaryWrapper}>
            <GridContainer {...styles.summaryContainer}>
                <GridItem {...styles.summaryLeftColumn}>
                    <StyledWrapper {...styles.customerAddress}>
                        <Typography {...styles.addressNameLine}>{summary.customer?.name}</Typography>
                        <Typography {...styles.addressLine}>{summary.customer?.address}</Typography>
                        <Typography
                            {...styles.addressLine}
                        >{`${summary.customer?.city}, ${summary.customer?.provinceState}`}</Typography>
                        <Typography {...styles.addressLine}>{summary.customer?.zipCode}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.customerInfoContainer}>
                        <StyledWrapper {...styles.customerInfoLine}>
                            <Typography {...styles.addressInfoLabel}>{translate("Company :")} </Typography>
                            <Typography {...styles.addressInfoValue}>{summary.customer?.company}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.customerInfoLine}>
                            <Typography {...styles.addressInfoLabel}>{translate("Number :")} </Typography>
                            <Typography {...styles.addressInfoValue}>{summary.customer?.number}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.customerInfoLine}>
                            <Typography {...styles.addressInfoLabel}>{translate("Terms :")} </Typography>
                            <Typography {...styles.addressInfoValue}>{summary?.terms}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.summaryMiddleColumn}>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("Current Month")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>
                                {summary.currentMonthPurchaseAmountDisplay}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("Current Year")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>
                                {summary.currentYearPurchaseAmountDisplay}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("Previous Year")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>
                                {summary.previousYearPurchaseAmountDisplay}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("Last Payment Date")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.lastPaymentDateDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.summaryRightColumn}>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("Future Amount")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.futureAmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("Current Amount")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.currentAmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("More than 30 days")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.period1AmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("More than 59 days")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.period2AmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("More than 89 days")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.period3AmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelText}>{translate("More than 119 days")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueText}>{summary.period4AmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.summaryLineContainer}>
                        <GridItem {...styles.summaryLineLabel}>
                            <Typography {...styles.summaryLineLabelTotalText}>{translate("Total Amount")}</Typography>
                        </GridItem>
                        <GridItem {...styles.summaryLineValue}>
                            <Typography {...styles.summaryLineValueTotalText}>{summary.totalAmountDisplay}</Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AccountSummary),
    definition: {
        displayName: "Account Summary",
        group: "Common",
    },
};

export default widgetModule;
